/**
 * PlotEmbed — standard charts via function-plot (math) or Plotly (scatter).
 * Replaces hand-rolled SVG sampling.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Maximize2, X } from 'lucide-react';
import functionPlot from 'function-plot';
import { CHAT_EMBED_PROPS } from '../../embeds/chat-embed.js';
import { parsePlotFenceBody, type PlotSpec } from '../../plots/plot-spec.js';
import { buildFunctionPlotOptions } from '../../lib/plot-function-plot.js';
import { PlotError } from './PlotError.js';

export interface PlotEmbedProps {
  body: string;
}

const INLINE_H = 280;

function Legend({ spec }: { spec: PlotSpec }) {
  const items = spec.series
    .map((s, i) => ({
      label: s.label,
      color: s.color ?? ['#0f766e', '#c2410c', '#1d4ed8', '#7c3aed'][i % 4]!,
    }))
    .filter((x) => x.label);
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 px-3 py-1.5 text-xs text-stone-600 dark:text-stone-300">
      {items.map((s, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-0.5 w-3 rounded"
            style={{ backgroundColor: s.color }}
          />
          {s.label}
        </span>
      ))}
    </div>
  );
}

function FunctionPlotHost({
  spec,
  height,
  className,
}: {
  spec: PlotSpec;
  height: number;
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    let cancelled = false;
    setError(null);

    const render = () => {
      if (cancelled || !hostRef.current) return;
      const host = hostRef.current;
      const width = Math.max(280, Math.floor(host.clientWidth || 640));
      host.innerHTML = '';
      try {
        const opts = buildFunctionPlotOptions(spec, host, { width, height });
        functionPlot(opts);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg.trim() || 'function-plot render failed');
      }
    };

    render();
    const ro = new ResizeObserver(() => render());
    ro.observe(el);
    return () => {
      cancelled = true;
      ro.disconnect();
      if (hostRef.current) hostRef.current.innerHTML = '';
    };
  }, [spec, height]);

  if (error) {
    return <PlotError message={error} />;
  }
  return (
    <div
      ref={hostRef}
      className={className}
      style={{ height, width: '100%', overflow: 'hidden' }}
    />
  );
}

function PlotlyHost({
  spec,
  height,
  className,
}: {
  spec: PlotSpec;
  height: number;
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    let cancelled = false;
    setError(null);

    void (async () => {
      try {
        const Plotly = (await import('plotly.js-dist-min')).default;
        if (cancelled || !hostRef.current) return;
        const host = hostRef.current;
        const traces = spec.series.map((s, i) => {
          if (s.type !== 'scatter') {
            throw new Error(`plotly engine expects scatter series, got ${s.type}`);
          }
          return {
            x: s.x,
            y: s.y,
            type: 'scatter' as const,
            mode: s.mode ?? 'lines+markers',
            name: s.label ?? s.id ?? `series ${i + 1}`,
            line: s.color ? { color: s.color, width: s.strokeWidth ?? 2 } : { width: s.strokeWidth ?? 2 },
            marker: s.color ? { color: s.color } : undefined,
          };
        });
        const layout = {
          title: spec.title ? { text: spec.title } : undefined,
          margin: { t: spec.title ? 40 : 24, r: 24, b: 48, l: 48 },
          xaxis: {
            range: [spec.viewport.xMin, spec.viewport.xMax],
            zeroline: true,
            title: 'x',
          },
          yaxis: {
            range: [spec.viewport.yMin, spec.viewport.yMax],
            zeroline: true,
            title: 'y',
            scaleanchor: spec.viewport.equalAspect ? ('x' as const) : undefined,
            scaleratio: spec.viewport.equalAspect ? 1 : undefined,
          },
          showlegend: spec.series.some((s) => s.label),
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
          font: { size: 12 },
        };
        await Plotly.newPlot(host, traces, layout, {
          displayModeBar: true,
          responsive: true,
          displaylogo: false,
        });
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg.trim() || 'plotly render failed');
      }
    })();

    return () => {
      cancelled = true;
      const host = hostRef.current;
      if (host) {
        void import('plotly.js-dist-min').then((m) => {
          try {
            m.default.purge(host);
          } catch {
            host.innerHTML = '';
          }
        });
      }
    };
  }, [spec, height]);

  if (error) {
    return <PlotError message={error} />;
  }
  return (
    <div
      ref={hostRef}
      className={className}
      style={{ height, width: '100%' }}
    />
  );
}

function ExpandOverlay({
  spec,
  onClose,
}: {
  spec: PlotSpec;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const engine = spec.engine ?? 'function-plot';

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/60 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Expanded plot"
      onClick={onClose}
    >
      <div
        className="mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-stone-200 bg-white shadow-xl dark:border-stone-700 dark:bg-stone-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-stone-200 px-3 py-2 dark:border-stone-700">
          <span className="truncate text-sm font-medium text-stone-800 dark:text-stone-100">
            {spec.title ?? 'Plot'}
            <span className="ml-2 text-xs font-normal text-stone-400">
              {engine === 'plotly' ? 'Plotly' : 'function-plot'}
            </span>
          </span>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-600 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 p-2">
          {engine === 'plotly' ? (
            <PlotlyHost spec={spec} height={560} className="h-full w-full" />
          ) : (
            <FunctionPlotHost spec={spec} height={560} className="h-full w-full" />
          )}
        </div>
        <Legend spec={spec} />
      </div>
    </div>
  );
}

export function PlotEmbed({ body }: PlotEmbedProps) {
  const [expanded, setExpanded] = useState(false);
  const parsed = useMemo(() => parsePlotFenceBody(body), [body]);

  if (!parsed.ok) {
    return <PlotError message={parsed.error} />;
  }

  const spec = parsed.spec;
  const engine = spec.engine ?? 'function-plot';
  const hasAnim = Boolean(spec.animation);

  return (
    <div
      {...CHAT_EMBED_PROPS}
      data-plot-embed
      className="my-3 w-full max-w-3xl overflow-hidden rounded-lg border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-900"
    >
      <div className="flex items-center justify-between gap-2 border-b border-stone-200 px-3 py-2 dark:border-stone-700">
        <span className="truncate text-sm font-medium text-stone-800 dark:text-stone-100">
          {spec.title ?? 'Plot'}
          <span className="ml-2 text-xs font-normal text-stone-400">
            {engine === 'plotly' ? 'Plotly' : 'function-plot'}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex shrink-0 items-center gap-1 text-xs text-sky-700 hover:underline dark:text-sky-400"
        >
          Expand
          <Maximize2 className="h-3 w-3" />
        </button>
      </div>
      {engine === 'plotly' ? (
        <PlotlyHost spec={spec} height={INLINE_H} />
      ) : (
        <FunctionPlotHost spec={spec} height={INLINE_H} />
      )}
      <Legend spec={spec} />
      {hasAnim ? (
        <div className="border-t border-stone-200 px-3 py-1.5 text-[11px] text-stone-500 dark:border-stone-700 dark:text-stone-400">
          Animation defined (v1 static snapshot at from)
        </div>
      ) : null}
      {expanded ? (
        <ExpandOverlay spec={spec} onClose={() => setExpanded(false)} />
      ) : null}
    </div>
  );
}

PlotEmbed.displayName = 'PlotEmbed';
